import {
  type CompanyData,
  isDefined,
  isDeleted,
  isEmpty,
  type TeamData,
  type TeamId,
  type TeamLevel as TeamLevelId,
} from '@terros/common'

export type CompanyLevels = CompanyData['levelNames']

export type TeamLevel = {
  level: number
  name: string
}

export function toTeamArray(companyLevels: CompanyLevels | undefined): TeamLevel[] {
  if (isEmpty(companyLevels)) return []
  const levels = Object.keys(companyLevels).map((val) => Number.parseInt(val, 10) || 0)
  levels.sort((a, b) => b - a)
  return levels.map((level) => {
    return {
      level,
      name: companyLevels[level],
    }
  })
}

export function getLevelForChildTeam(parentLevel: number | undefined): TeamLevelId {
  switch (parentLevel) {
    case 6:
      return 5
    case 5:
      return 4
    case 4:
      return 3
    case 3:
      return 2
    default:
      return 1
  }
}

export class Teams {
  private readonly _teams: Team[]
  private readonly _highestTeamLevel: number

  constructor(teams: TeamData[] | undefined) {
    this._teams = (teams ?? []).map((team) => new Team(team))
    this._teams.sort((a, b) => a.name.localeCompare(b.name))
    this._highestTeamLevel = Math.max(...this._teams.map((t) => t.level))

    this._teams.forEach((child) => {
      if (child.isTop()) return
      const { parentId } = child.toData()
      this._teams.forEach((parent) => {
        if (parent.teamId === parentId) {
          parent.addChild(child)
        }
      })
    })
  }

  public getTopTeams(): Team[] {
    return this._teams.filter((t) => t.isTop())
  }

  private isRootInDataset(team: Team): boolean {
    if (!team.hasParent()) return true
    const parent = this._teams.find((p) => p.teamId === team.toData().parentId)
    return !parent
  }

  public getRootTeams(): Team[] {
    return this._teams.filter((t) => this.isRootInDataset(t))
  }

  public filter(filter: string): Team[] {
    const f = filter.toLowerCase()
    return this._teams.filter((t) => this.isRootInDataset(t) && t.filter(f)).map((t) => t.toFiltered(f))
  }
}

export class Team {
  private readonly _data: TeamData
  private readonly _children: Team[] = []

  constructor(team: TeamData) {
    this._data = team
  }

  get teamId(): TeamId {
    return this._data.teamId
  }

  get name(): string {
    return this._data.name
  }

  get level(): number {
    return this._data.level ?? 0
  }

  get children(): Team[] {
    return this._children
  }

  get isDeleted(): boolean {
    return isDeleted(this._data)
  }

  public canMutate(manages: TeamId[]): boolean {
    const parentTeamIds = this._data.parentTeamIds ?? []
    return manages.includes(this.teamId) || parentTeamIds.some((id) => manages.includes(id))
  }

  public isTop(): boolean {
    return !this.hasParent()
  }

  public hasParent(): boolean {
    return isDefined(this._data.parentId)
  }

  public addChild(team: Team): void {
    this._children.push(team)
  }

  public filter(filter: string): boolean {
    return this.name.toLowerCase().includes(filter) || this.children.some((t) => t.filter(filter))
  }

  public toFiltered(filter: string): Team {
    const newTeam = new Team(this._data)
    const children = this._children.filter((t) => t.filter(filter)).map((t) => t.toFiltered(filter))
    children.forEach((t) => {
      newTeam.addChild(t)
    })
    return newTeam
  }

  public toData(): TeamData {
    return this._data
  }
}
